import { Grid } from '@mui/material';
import { dateFormatLongDate } from '@nx-smartmonkey/shared/helpers';
import { HSpacer, RoutalPalette, RoutalSelect, RoutalSelectMultiSelectOption } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { routeService } from '../../../application/RouteService';
import DraftPlanIcon from '../../../assets/svg/plan-status/DraftPlanIcon';
import FinishedPlanIcon from '../../../assets/svg/plan-status/FinishedPlanIcon';
import PlanningPlanIcon from '../../../assets/svg/plan-status/PlanningPlanIcon';
import { FlatRoute } from '../../../domain/route/FlatRoute';
import { RouteStatus } from '../../../domain/route/RouteStatus';
import { intl } from '../../../helpers/IntGlobalProvider';
import { Text } from '../../../react-components/Text';

interface RouteProps {
  destinationRouteId?: string;
  selectedRoute?: FlatRoute;
  disabled?: boolean;
  onChange: (route: FlatRoute) => void;
}

type RouteStatusIconProps = {
  status: RouteStatus;
  style?: React.CSSProperties;
};
const getPlanStatusColor = (status: RouteStatus, type?: `light` | `medium` | `dark`) => {
  switch (status) {
    case `finished`:
      return RoutalPalette.planStatusColors.finished[type ?? `medium`];
    case `not_started`:
      return RoutalPalette.planStatusColors.planning[type ?? `medium`];
    case `in_transit`:
      return RoutalPalette.planStatusColors.in_progress[type ?? `medium`];
    default:
      return RoutalPalette.planStatusColors.draft[type ?? `medium`];
  }
};
const RouteStatusIcon = ({ status, style }: RouteStatusIconProps) => {
  const color = getPlanStatusColor(status, `dark`);
  return status === `not_started` ? (
    <DraftPlanIcon style={style} color={color} />
  ) : status === `in_transit` ? (
    <PlanningPlanIcon style={style} color={color} />
  ) : (
    <FinishedPlanIcon style={style} color={color} />
  );
};

export const RouteSelector = ({ onChange, selectedRoute, disabled, destinationRouteId }: RouteProps) => {
  const [routes, setRoutes] = useState<FlatRoute[] | undefined>(undefined);

  useEffect(() => {
    (async () => {
      if (!routes) {
        const response = await routeService.searchFlatRoutes({
          limit: 250,
          sort_by: `execution_date`,
        });

        const retrievedRoutes = (response.docs as FlatRoute[]).filter((route) => route.id !== destinationRouteId);

        setRoutes(retrievedRoutes);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes]);

  return (
    <RoutalSelect
      id="select-route"
      style={{
        backgroundColor: `transparent`,
        height: `49px`,
      }}
      disabled={disabled || (routes && routes.length === 0)}
      hideSeparator
      popoverProps={{
        options: (routes?.filter((r) => r.id !== destinationRouteId && r.id !== selectedRoute?.id) ?? []).map(
          (r, index): RoutalSelectMultiSelectOption => {
            return {
              key: `option-${index}`,
              listItemStyles: {
                whiteSpace: `normal` as any,
                fontSize: `12px`,
              },
              onClick: () => {
                onChange(r);
              },
              content: (
                <Grid style={{ display: `flex`, flexDirection: `row`, alignItems: `center` }}>
                  {r.status && (
                    <>
                      <Grid
                        style={{
                          display: `flex`,
                          padding: `6px 10px`,
                          borderRadius: `5px`,
                          border: `1px solid ${getPlanStatusColor(r.status, `medium`)}`,
                          backgroundColor: getPlanStatusColor(r.status, `light`),
                          alignItems: `center`,
                        }}
                      >
                        <RouteStatusIcon status={r.status} />
                      </Grid>
                      <HSpacer small />
                    </>
                  )}

                  <Grid style={{ display: `flex`, flexDirection: `column`, justifyContent: `center` }}>
                    <Text
                      style={{
                        cursor: `pointer`,
                        width: `100%`,
                        textAlign: `justify`,
                        color: r.plan_label || r.label ? RoutalPalette.text : RoutalPalette.secondary00,
                      }}
                    >
                      {r?.plan_label ??
                        r?.label ??
                        intl.formatMessage({
                          id: `driver.stop.route_selector.no_label`,
                        })}
                    </Text>
                    {r.plan_execution_date && (
                      <Grid>
                        <Text variant="label" style={{ cursor: `pointer`, width: `100%`, textAlign: `justify` }}>
                          {dateFormatLongDate(r.plan_execution_date)}
                        </Text>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              ),
            };
          }
        ),
      }}
    >
      <Text style={{ cursor: `pointer`, width: `100%`, textAlign: `justify` }}>
        {selectedRoute?.plan_label ??
          intl.formatMessage({
            id:
              routes && routes.filter((route) => route.id !== destinationRouteId).length === 0
                ? `driver.stop.route_selector.placeholder.empty`
                : `driver.stop.route_selector.placeholder`,
          })}
      </Text>
    </RoutalSelect>
  );
};
